<template>
  <div>
    <HrPageLayout
      title="Appraisal Details"
      subtitle="View your appraisal"
      icon="📋"
      :breadcrumb-items="[
        { label: 'Dashboard', route: '/employee-dashboard' },
        { label: 'My Appraisals', route: '/my-appraisals' },
        { label: 'Details' }
      ]"
    >
      <div class="appraisal-details-content">
        <div v-if="loading" class="hr-loading-wrap hr-py-5">
          <div class="hr-spinner"></div>
          <p class="hr-text-muted hr-mt-2">Loading appraisal details...</p>
        </div>

        <template v-else-if="appraisal">

          <!-- Header Card -->
          <div class="hr-chart-card hr-mb-4">
            <div class="hr-chart-body">
              <div class="appraisal-detail-header">
                <div>
                  <h5 class="hr-mb-2">{{ appraisal.employeeName }}</h5>
                  <p class="hr-text-muted hr-mb-1"><strong>Position:</strong> {{ appraisal.positionTitle }}</p>
                  <p class="hr-text-muted hr-mb-1"><strong>Department:</strong> {{ appraisal.department }}</p>
                  <p class="hr-text-muted hr-mb-0"><strong>Review Period:</strong> {{ appraisal.periodStart }} - {{ appraisal.periodEnd }}</p>
                </div>
                <div class="hr-text-end">
                  <span class="hr-badge hr-mb-2" :class="getStatusBadgeClass(appraisal.status)">{{ appraisal.status }}</span>
                  <div v-if="appraisal.finalOverallRating">
                    <h3 class="hr-mb-0">{{ parseFloat(appraisal.finalOverallRating).toFixed(2) }}/5</h3>
                    <small class="hr-text-muted">Overall Rating</small>
                    <span v-if="appraisal.performance_category" :class="['hr-badge hr-ms-2', getCategoryBadgeClass(appraisal.performance_category)]">{{ appraisal.performance_category }}</span>
                  </div>
                  <div v-if="appraisal.status === 'Approved' && appraisal.acknowledged_at" class="hr-mt-2 hr-small hr-text-success">
                    <i class="bx bx-check-circle hr-me-1"></i> Acknowledged {{ formatDateTime(appraisal.acknowledged_at) }}
                  </div>
                  <div class="hr-mt-3">
                    <button type="button" class="hr-btn hr-btn-sm hr-btn-outline hr-btn-primary hr-me-2" @click="printAppraisal">
                      <i class="bx bx-printer"></i> Print
                    </button>
                    <button v-if="appraisal.status === 'Approved'" type="button" class="hr-btn hr-btn-sm hr-btn-primary hr-me-2" @click="downloadPDF">
                      <i class="bx bx-download"></i> Download PDF
                    </button>
                    <button v-if="appraisal.status === 'Approved' && !appraisal.acknowledged_at" type="button" class="hr-btn hr-btn-sm hr-btn-success" @click="acknowledgeAppraisal" :disabled="acknowledging">
                      <i class="bx bx-check"></i> {{ acknowledging ? 'Acknowledging...' : 'Acknowledge' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Section A: KPAs -->
          <div v-if="kpas.length > 0" class="hr-chart-card hr-mb-4">
            <div class="hr-chart-header hr-bg-light">
              <h5 class="hr-chart-title hr-mb-0">
                <i class="bx bx-target-lock hr-me-2 hr-text-primary"></i>
                Section A: Key Performance Areas
              </h5>
            </div>
            <div class="hr-chart-body">
              <div class="hr-table-wrapper">
                <table class="hr-table hr-table-bordered">
                  <thead>
                    <tr>
                      <th width="5%">#</th>
                      <th width="30%">KPA</th>
                      <th width="30%">Results Achieved</th>
                      <th width="10%">Self Rating</th>
                      <th width="10%">Weightage</th>
                      <th width="10%">Appraiser Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="kpa in kpas" :key="kpa.id">
                      <td class="hr-text-center">{{ kpa.sNo }}</td>
                      <td>{{ kpa.kpa }}</td>
                      <td>{{ kpa.resultsAchieved || '-' }}</td>
                      <td class="hr-text-center"><span class="hr-badge hr-badge-primary">{{ kpa.selfRating || '-' }}</span></td>
                      <td class="hr-text-center">{{ kpa.weightage }}%</td>
                      <td class="hr-text-center">
                        <span v-if="kpa.appraiserRating" class="hr-badge hr-badge-success">{{ kpa.appraiserRating }}</span>
                        <span v-else class="hr-text-muted">Pending</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-if="organizationalParams.length > 0" class="hr-mt-4">
                <h6 class="hr-text-primary hr-mb-3">Key Organizational Parameters</h6>
                <div class="hr-table-wrapper">
                  <table class="hr-table hr-table-bordered">
                    <thead>
                      <tr>
                        <th width="5%">#</th>
                        <th width="45%">Question</th>
                        <th width="10%">Answer</th>
                        <th width="40%">Comments</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="param in organizationalParams" :key="param.id">
                        <td class="hr-text-center">{{ param.sNo }}</td>
                        <td>{{ param.question }}</td>
                        <td class="hr-text-center"><span class="hr-badge hr-badge-primary">{{ param.answer || '-' }}</span></td>
                        <td>{{ param.associateComments || '-' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- Section B: Attributes -->
          <div v-if="attributes.length > 0" class="hr-chart-card hr-mb-4">
            <div class="hr-chart-header hr-bg-light">
              <h5 class="hr-chart-title hr-mb-0">
                <i class="bx bx-star hr-me-2 hr-text-primary"></i>
                Section B: Critical Success Attributes
              </h5>
            </div>
            <div class="hr-chart-body">
              <div class="hr-table-wrapper">
                <table class="hr-table hr-table-bordered">
                  <thead>
                    <tr>
                      <th width="5%">#</th>
                      <th width="40%">Attribute</th>
                      <th width="15%">Self Rating</th>
                      <th width="15%">Appraiser Rating</th>
                      <th width="25%">Appraiser Comments</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="attr in attributes" :key="attr.id">
                      <td class="hr-text-center">{{ attr.sNo }}</td>
                      <td>{{ attr.attribute }}</td>
                      <td class="hr-text-center"><span class="hr-badge hr-badge-primary">{{ attr.selfRating || '-' }}</span></td>
                      <td class="hr-text-center">
                        <span v-if="attr.appraiserRating" class="hr-badge hr-badge-success">{{ attr.appraiserRating }}</span>
                        <span v-else class="hr-text-muted">Pending</span>
                      </td>
                      <td>{{ attr.appraiserComment || '-' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Section C: Associate Feedback -->
          <div class="hr-chart-card hr-mb-4">
            <div class="hr-chart-header hr-bg-light">
              <h5 class="hr-chart-title hr-mb-0">
                <i class="bx bx-message-detail hr-me-2 hr-text-primary"></i>
                Section C: Associate Feedback
              </h5>
            </div>
            <div class="hr-chart-body">
              <div v-if="jobContent.length > 0" class="hr-mb-4">
                <h6 class="hr-bg-light hr-p-2">Job Content</h6>
                <div class="hr-table-wrapper">
                  <table class="hr-table hr-table-sm">
                          <tbody>
                          <tr v-for="item in jobContent" :key="item.id">
                            <td width="50%">{{ item.attribute }}</td>
                            <td width="10%" class="hr-text-center">
                              <span :class="['hr-badge', item.response === 'Yes' ? 'hr-badge-success' : 'hr-badge-secondary']">{{ item.response }}</span>
                            </td>
                            <td width="40%">{{ item.remarks || '-' }}</td>
                          </tr>
                          </tbody>
                        </table>
                </div>
              </div>
              <div v-if="growthOpportunities.length > 0" class="hr-mb-4">
                <h6 class="hr-bg-light hr-p-2">Growth Opportunities</h6>
                <div class="hr-table-wrapper">
                  <table class="hr-table hr-table-sm">
                    <tbody>
                      <tr v-for="item in growthOpportunities" :key="item.id">
                        <td width="50%">{{ item.attribute }}</td>
                        <td width="10%" class="hr-text-center"><span :class="['hr-badge', item.response === 'Yes' ? 'hr-badge-success' : 'hr-badge-secondary']">{{ item.response }}</span></td>
                        <td width="40%">{{ item.remarks || '-' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-if="workCulture.length > 0" class="hr-mb-4">
                <h6 class="hr-bg-light hr-p-2">Work Culture</h6>
                <div class="hr-table-wrapper">
                  <table class="hr-table hr-table-sm">
                    <tbody>
                      <tr v-for="item in workCulture" :key="item.id">
                        <td width="50%">{{ item.attribute }}</td>
                        <td width="10%" class="hr-text-center"><span :class="['hr-badge', item.response === 'Yes' ? 'hr-badge-success' : 'hr-badge-secondary']">{{ item.response }}</span></td>
                        <td width="40%">{{ item.remarks || '-' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-if="colleagues.length > 0" class="hr-mb-4">
                <h6 class="hr-bg-light hr-p-2">Colleagues/Team Members</h6>
                <div class="hr-table-wrapper">
                  <table class="hr-table hr-table-sm">
                    <tbody>
                      <tr v-for="item in colleagues" :key="item.id">
                        <td width="50%">{{ item.attribute }}</td>
                        <td width="10%" class="hr-text-center"><span :class="['hr-badge', item.response === 'Yes' ? 'hr-badge-success' : 'hr-badge-secondary']">{{ item.response }}</span></td>
                        <td width="40%">{{ item.remarks || '-' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-if="trainings.length > 0" class="hr-mb-4">
                <h6 class="hr-bg-light hr-p-2">Training Priorities</h6>
                <div class="hr-table-wrapper">
                  <table class="hr-table hr-table-sm">
                    <thead><tr><th>Training Program</th><th width="20%" class="hr-text-center">Priority</th></tr></thead>
                    <tbody>
                      <tr v-for="item in trainings" :key="item.id">
                        <td>{{ item.trainingProgram }}</td>
                        <td class="hr-text-center"><span class="hr-badge hr-badge-primary">{{ item.priority }}</span></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-if="additionalFeedback" class="hr-mb-4">
                <h6 class="hr-bg-light hr-p-2">Additional Feedback</h6>
                <div class="hr-form-grid">
                  <div class="hr-mb-3"><label class="hr-form-label hr-fw-bold">Things I Should Do:</label><p class="hr-mb-0">{{ additionalFeedback.thingsIShouldDo || '-' }}</p></div>
                  <div class="hr-mb-3"><label class="hr-form-label hr-fw-bold">Manager Should Do:</label><p class="hr-mb-0">{{ additionalFeedback.managerShouldDo || '-' }}</p></div>
                  <div class="hr-mb-3"><label class="hr-form-label hr-fw-bold">Organization Should Do:</label><p class="hr-mb-0">{{ additionalFeedback.organizationShouldDo || '-' }}</p></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Section E: Goals -->
          <div v-if="goals.length > 0" class="hr-chart-card hr-mb-4">
            <div class="hr-chart-header hr-bg-light">
              <h5 class="hr-chart-title hr-mb-0">
                <i class="bx bx-flag hr-me-2 hr-text-primary"></i>
                Section E: Goals for Next Review Period
              </h5>
            </div>
            <div class="hr-chart-body">
              <div class="hr-table-wrapper">
                <table class="hr-table hr-table-bordered">
                  <thead>
                    <tr>
                      <th width="5%">#</th>
                      <th width="75%">Action Plan</th>
                      <th width="20%">Target Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="goal in goals" :key="goal.id">
                      <td class="hr-text-center">{{ goal.sNo }}</td>
                      <td>{{ goal.actionPlan }}</td>
                      <td>{{ formatDate(goal.targetDate) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Comments -->
          <div v-if="appraisal.associateComments || appraisal.reviewerComments" class="hr-chart-card hr-mb-4">
            <div class="hr-chart-header hr-bg-light">
              <h5 class="hr-chart-title hr-mb-0">
                <i class="bx bx-message-rounded hr-me-2 hr-text-primary"></i>
                Comments
              </h5>
            </div>
            <div class="hr-chart-body">
              <div class="appraisal-comments-row">
                <div v-if="appraisal.associateComments"><h6 class="hr-text-primary">Associate Comments:</h6><p class="hr-mb-0">{{ appraisal.associateComments }}</p></div>
                <div v-if="appraisal.reviewerComments"><h6 class="hr-text-success">Reviewer Comments:</h6><p class="hr-mb-0">{{ appraisal.reviewerComments }}</p></div>
              </div>
            </div>
          </div>

          <!-- Workflow History -->
          <div v-if="workflowLogs.length > 0" class="hr-chart-card hr-mb-4">
            <div class="hr-chart-header hr-bg-light">
              <h5 class="hr-chart-title hr-mb-0">
                <i class="bx bx-time-five hr-me-2 hr-text-primary"></i>
                Workflow History
              </h5>
            </div>
            <div class="hr-chart-body">
              <div class="timeline">
                <div v-for="log in workflowLogs" :key="log.id" class="timeline-item hr-mb-3">
                  <div class="hr-flex">
                    <div class="hr-flex-shrink-0">
                      <span class="timeline-dot hr-bg-primary"><i class="bx bx-check"></i></span>
                    </div>
                    <div class="hr-flex-grow-1 hr-ms-3">
                      <h6 class="hr-mb-1">{{ log.action }}</h6>
                      <p class="hr-text-muted hr-mb-1">{{ log.fromStatus }} → {{ log.toStatus }}</p>
                      <small class="hr-text-muted">By {{ log.performedBy }} on {{ formatDateTime(log.createdAt) }}</small>
                      <p v-if="log.comments" class="hr-mt-2 hr-mb-0">{{ log.comments }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="hr-mb-4 hr-text-end">
            <router-link to="/my-appraisals" class="hr-btn hr-btn-secondary">
              <i class="bx bx-arrow-back"></i> Back to List
            </router-link>
            <button v-if="appraisal.status === 'Draft'" type="button" class="hr-btn hr-btn-primary hr-ms-2" @click="editAppraisal">
              <i class="bx bx-edit"></i> Edit
            </button>
          </div>
        </template>

        <div v-else class="hr-chart-card hr-text-center hr-py-5">
          <i class="bx bx-error-circle" style="font-size: 48px; color: #64748b;"></i>
          <h5 class="hr-mt-4">Appraisal Not Found</h5>
          <p class="hr-text-muted">The appraisal you're looking for doesn't exist or you don't have access to it.</p>
          <router-link to="/my-appraisals" class="hr-btn hr-btn-primary hr-mt-3">
            <i class="bx bx-arrow-back"></i> Back to My Appraisals
          </router-link>
        </div>
      </div>
    </HrPageLayout>
  </div>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "AppraisalDetails",
  components: { HrPageLayout },

  data() {
    return {
      loading: false,
      acknowledging: false,
      appraisalId: null,
      appraisal: null,
      kpas: [],
      organizationalParams: [],
      attributes: [],
      jobContent: [],
      growthOpportunities: [],
      workCulture: [],
      colleagues: [],
      trainings: [],
      additionalFeedback: null,
      goals: [],
      workflowLogs: []
    };
  },

  mounted() {
    this.appraisalId = this.$route.query.id;
    if (this.appraisalId) {
      this.loadAppraisalDetails();
    }
  },

  methods: {
    async loadAppraisalDetails() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getAppraisalDetails");
        data.append("appraisalId", this.appraisalId);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          const details = res.data.data;

          this.appraisal = details.appraisal;
          this.kpas = details.kpas || [];
          this.organizationalParams = details.organizationalParams || [];
          this.attributes = details.attributes || [];
          this.jobContent = details.jobContent || [];
          this.growthOpportunities = details.growthOpportunities || [];
          this.workCulture = details.workCulture || [];
          this.colleagues = details.colleagues || [];
          this.trainings = details.trainings || [];
          this.additionalFeedback = details.additionalFeedback;
          this.goals = details.goals || [];
          this.workflowLogs = details.workflowLogs || [];
        } else {
          this.appraisal = null;
        }
      } catch (error) {
        console.error("Failed to load appraisal details:", error);
        this.$toast?.error("Failed to load appraisal details") || alert("Failed to load appraisal details");
      } finally {
        this.loading = false;
      }
    },

    editAppraisal() {
      this.$router.push({
        path: '/employee-self-assessment',
        query: { id: this.appraisalId }
      });
    },

    printAppraisal() {
      window.print();
    },

    downloadPDF() {
      window.open(`${authUrl}?function=downloadAppraisalPDF&appraisalId=${this.appraisalId}`, '_blank');
    },

    getStatusBadgeClass(status) {
      const map = {
        'Draft': 'hr-badge-secondary',
        'Self-Assessment Complete': 'hr-badge-info',
        'Appraiser Review Pending': 'hr-badge-warning',
        'Appraiser Review Complete': 'hr-badge-primary',
        'Reviewer Pending': 'hr-badge-warning',
        'Reviewer Complete': 'hr-badge-primary',
        'HR/CEO Review Pending': 'hr-badge-warning',
        'Approved': 'hr-badge-success',
        'Rejected': 'hr-badge-danger'
      };
      return map[status] || 'hr-badge-secondary';
    },
    getCategoryBadgeClass(category) {
      const c = (category || '').toLowerCase();
      if (c.includes('excellent')) return 'hr-badge-success';
      if (c.includes('very good')) return 'hr-badge-primary';
      if (c.includes('good')) return 'hr-badge-info';
      if (c.includes('improvement')) return 'hr-badge-warning';
      return 'hr-badge-secondary';
    },

    async acknowledgeAppraisal() {
      this.acknowledging = true;
      try {
        const data = new FormData();
        data.append('function', 'acknowledgeAppraisal');
        data.append('appraisalId', this.appraisalId);
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.$toast?.success?.('Appraisal acknowledged.') || alert('Appraisal acknowledged.');
          this.appraisal.acknowledged_at = new Date().toISOString().slice(0, 19).replace('T', ' ');
          this.appraisal.acknowledged_by = res.data.data?.acknowledged_by || '';
        } else {
          this.$toast?.error?.(res?.data?.message || 'Failed to acknowledge') || alert(res?.data?.message || 'Failed to acknowledge');
        }
      } catch (e) {
        this.$toast?.error?.('Failed to acknowledge') || alert('Failed to acknowledge');
      } finally {
        this.acknowledging = false;
      }
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    },

    formatDateTime(datetime) {
      if (!datetime) return '-';
      return new Date(datetime).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";
.appraisal-detail-header { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 16px; }
.hr-text-end { text-align: right; }
.hr-mb-0 { margin-bottom: 0; }
.hr-mb-1 { margin-bottom: 4px; }
.hr-mb-2 { margin-bottom: 8px; }
.hr-mt-2 { margin-top: 8px; }
.hr-mt-3 { margin-top: 12px; }
.hr-mt-4 { margin-top: 16px; }
.hr-mb-3 { margin-bottom: 12px; }
.hr-mb-4 { margin-bottom: 16px; }
.hr-ms-2 { margin-left: 8px; }
.hr-me-1 { margin-right: 4px; }
.hr-me-2 { margin-right: 8px; }
.hr-bg-light { background: #f8fafc; }
.hr-p-2 { padding: 8px 12px; }
.hr-text-primary { color: #4f46e5; }
.hr-text-success { color: #10b981; }
.hr-text-muted { color: #64748b; }
.hr-small { font-size: 0.875rem; }
.hr-form-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 16px; }
.hr-fw-bold { font-weight: 600; }
.appraisal-comments-row { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 24px; }
.timeline-item { position: relative; }
.timeline-item:not(:last-child)::before {
  content: '';
  position: absolute;
  left: 11px;
  top: 28px;
  width: 2px;
  height: calc(100% + 12px);
  background: #e2e8f0;
}
.timeline-dot { width: 24px; height: 24px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; color: #fff; font-size: 12px; }
.hr-bg-primary { background: #4f46e5; }
.hr-flex { display: flex; }
.hr-flex-shrink-0 { flex-shrink: 0; }
.hr-flex-grow-1 { flex-grow: 1; }
.hr-ms-3 { margin-left: 12px; }
@media print {
  .hr-btn, .modern-page-header, .breadcrumb { display: none !important; }
  .hr-chart-card { break-inside: avoid; page-break-inside: avoid; }
}
</style>